import {ProModal, ProModalProps} from 'Components/ProModal';
import ProForm from 'Components/ProForm';
import {Input, message} from 'antd';
import CustomRules from 'Utils/rules';
import {GenderDict, getUserDetail, saveUser, UserFormData} from '../../service';
import {useDict} from '../../store';
import {useForm, useWatch} from 'antd/es/form/Form';
import {useMemo} from 'react';
import {UploadSinglePhoto} from 'Features/Upload/UploadSinglePhoto';
import useRequestFn from 'Utils/Hooks/useRequestFn';
import {errorMessage} from 'Utils/common';
import {useMount} from 'react-use';
import SelectDict from 'Features/Select/SelectDict';

interface UserModalProps extends ProModalProps {
    id?: string;
    onOk(): void;
}

export function UserModal({onOk, id, ...props}: UserModalProps) {
    const [form] = useForm<UserFormData>();

    const dict = useDict();

    const roleIds = useWatch('roleId', form);

    useMount(async () => {
        if (!id) return;

        try {
            const data = await getUserDetail(id);
            form.setFieldsValue(data);
        } catch (e) {
            message.error(errorMessage(e));
        }
    });

    const defaultRoleList = useMemo(
        () => dict.RoleList.data.filter(item => roleIds?.includes(item.value)),
        [dict.RoleList.data, roleIds],
    );

    function onSelectRoleIds(ids: string[]) {
        if (ids.length === 1) {
            form.setFieldValue('defaultRoleId', ids[0]);
        }

        const defaultRoleId = form.getFieldValue('defaultRoleId');

        if (!ids.includes(defaultRoleId)) {
            form.setFieldValue('defaultRoleId', undefined);
        }
    }

    const [onFinish] = useRequestFn(async (data: UserFormData) => {
        try {
            await saveUser(data);
            message.success('保存成功!');
            onOk();
        } catch (e) {
            message.error(errorMessage(e));
        }
    });

    return (
        <ProModal.Form
            {...props}
            form={form}
            onFinish={onFinish}
            title={id ? `修改用户` : '新增用户'}
            width={800}
            formProps={{columns: 1, wrapperCol: {span: 13, offset: 1}}}>
            <ProForm.Field name="id" hidden>
                <Input />
            </ProForm.Field>

            <ProForm.Field
                name="code"
                label="用户账号"
                required
                rules={[
                    {required: true, message: '${label}必填'},
                    {
                        type: 'string',
                        min: 8,
                        max: 20,
                        message:
                            '${label}必填,且最少 8 个最多20个半角字符,可包含半角数字、大小写字母,但不能使用特殊字符“/\\[]:;|=,+#*?<>”',
                    },
                ]}>
                <Input
                    placeholder="请输入用户工号"
                    maxLength={100}
                    disabled={!!id}
                />
            </ProForm.Field>

            <ProForm.Field
                name="userName"
                label="用户姓名"
                required
                rules={[{required: true, message: '${label}不能为空'}]}>
                <Input placeholder="请输入用户姓名" maxLength={100} />
            </ProForm.Field>

            <ProForm.Field name="roleId" label="角色分配">
                <SelectDict
                    mode="multiple"
                    placeholder="请选择角色"
                    options={dict.RoleList.data}
                    onChange={onSelectRoleIds}
                />
            </ProForm.Field>

            <ProForm.Field name="defaultRoleId" label="默认角色">
                <SelectDict
                    placeholder="请选择默认角色"
                    options={defaultRoleList}
                    allowClear
                />
            </ProForm.Field>

            <ProForm.Field name="photo" label="头像">
                <UploadSinglePhoto />
            </ProForm.Field>

            <ProForm.Field
                name="gender"
                label="性别"
                required
                rules={[{required: true, message: '${label}不能为空'}]}>
                <SelectDict
                    placeholder="请选择性别"
                    options={GenderDict.data}
                />
            </ProForm.Field>

            <ProForm.Field
                name="email"
                label="邮箱"
                rules={[{type: 'email', message: '请输入正确格式的${label}'}]}>
                <Input placeholder="请输入邮箱" maxLength={100} />
            </ProForm.Field>

            <ProForm.Field
                name="phone"
                label="手机号码"
                rules={[CustomRules.MobilePhone]}>
                <Input placeholder="请输入邮箱" maxLength={100} />
            </ProForm.Field>

            <ProForm.Field name="tel" label="座机">
                <Input placeholder="请输入邮箱" maxLength={100} />
            </ProForm.Field>
        </ProModal.Form>
    );
}
